import React, { useState, useEffect } from 'react';
import { X, Search, Calendar, Clock, User, Plus, Trash2, Briefcase, FileText } from 'lucide-react';
import DateTimePicker from './DateTimePicker';
import AddressFinder from './AddressFinder';
import JobCategoryPicker from './JobCategoryPicker';
import jobCategoryService from '../services/jobCategoryService';
import { formatDuration } from '../utils/formatDuration';
import customerService from '../services/customerService';
import appointmentService from '../services/appointmentService';
import userService from '../services/userService';
import { getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from '../App/services/appHelpers';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';

const CONTRACT_SERVICES_PARENT_ID = '1e0c8b89-7da1-11f0-9c5b-b23beb094b2b';

const AddJobModal = ({ onClose, onSubmit, job, isEditing = false, preSelectedCustomer = null, user, onAppointmentChange, mode = 'job' }) => {
  // Helper function to format customer address
  const formatCustomerAddress = (customer) => {
    if (!customer) return '';
    const parts = [
      customer.address_line1,
      customer.address_city,
      customer.address_postcode
    ].filter(Boolean);
    return parts.join(', ') || 'No address';
  };

  const [isContract, setIsContract] = useState(mode === 'contract');

  const [formData, setFormData] = useState({
    description: job?.description || '',
    customer_id: preSelectedCustomer?.id || job?.customer_id || '',
    job_category_id: isContract ? CONTRACT_SERVICES_PARENT_ID : (job?.job_category_id || ''),
    status: job?.status || 'active',
    price: job?.price || '',
    estimated_visits: job?.estimated_visits || (isContract ? 4 : ''),
    price_type: job?.price_type || (isContract ? 'per_year' : 'total'),
    contract_start_date: job?.contract_start_date || (isContract ? new Date().toISOString().split('T')[0] : ''),
    notes: job?.notes || '',
    // Treatment contact details
    treatment_contact_name: job?.treatment_contact_name || '',
    treatment_contact_company: job?.treatment_contact_company || '',
    treatment_contact_email: job?.treatment_contact_email || '',
    treatment_contact_phone: job?.treatment_contact_phone || '',
    // Treatment address
    treatment_address_line1: job?.treatment_address_line1 || '',
    treatment_address_line2: job?.treatment_address_line2 || '',
    treatment_address_city: job?.treatment_address_city || '',
    treatment_address_state: job?.treatment_address_state || '',
    treatment_address_postcode: job?.treatment_address_postcode || '',
    treatment_address_country: job?.treatment_address_country || 'United Kingdom',
    address_source: job?.address_source || 'manual',
    use_customer_address: job?.use_customer_address ?? (!isEditing && preSelectedCustomer?.address_line1 ? true : false),
    technician_id: job?.technician_id || '',
    // Optional first appointment
    schedule_first_appointment: false,
    appointment_date: '',
    appointment_time: '',
    appointment_duration: 60,
    appointment_notes: ''
  });

  const { confirmDialog, confirm } = useConfirm();
  const [errors, setErrors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [selectedCategoryPath, setSelectedCategoryPath] = useState('');
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [contractSubcategories, setContractSubcategories] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const isAdmin = user?.role === 'admin';

  // Load technicians for admin users
  useEffect(() => {
    if (!isAdmin) return;
    const loadTechnicians = async () => {
      try {
        const result = await userService.getUsers({ role: 'technician', active: true, limit: 100 });
        setTechnicians(result.users || []);
      } catch (error) {
        console.error('Error loading technicians:', error);
      }
    };
    loadTechnicians();
  }, [isAdmin]);

  // Load contract subcategories when in contract mode
  useEffect(() => {
    if (isContract) {
      setSelectedCategoryPath('Contract Services');
      const loadContractSubs = async () => {
        try {
          const allCategories = await jobCategoryService.getJobCategories();
          const subs = allCategories.filter(c => c.parent_id === CONTRACT_SERVICES_PARENT_ID);
          setContractSubcategories(subs);
        } catch (error) {
          console.error('Error loading contract subcategories:', error);
        }
      };
      loadContractSubs();
    }
  }, [isContract]);

  // Helper function to get current hour with minute 0
  const getCurrentTimeRounded = () => {
    const now = new Date();
    return {
      hour: String(now.getHours()).padStart(2, '0'),
      minute: '00'
    };
  };

  // Status options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Load customer when editing a job or when preSelectedCustomer is provided
  useEffect(() => {
    const loadCustomer = async () => {
      if (preSelectedCustomer) {
        setCustomers([preSelectedCustomer]);
      } else if (job && isEditing && job.customer_id && user) {
        // Load the customer for the job being edited
        try {
          const franchiseeId = getFranchiseeIdForCRUD(user, job.franchisee_id);
          const result = await customerService.getCustomer(franchiseeId, job.customer_id);
          const customer = result?.customer || result;
          if (customer && customer.id) {
            setCustomers([customer]);
          }
        } catch (error) {
          console.error('Error loading customer:', error);
        }
      }
    };

    loadCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.customer_id, preSelectedCustomer, isEditing]);

  // Debounced search effect - searches through ALL customers in database
  useEffect(() => {
    const searchCustomers = async () => {
      if (!customerSearchTerm || customerSearchTerm.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      try {
        const franchiseeId = getFranchiseeIdForDataLoading(user);
        const response = await customerService.searchCustomers(
          franchiseeId,
          customerSearchTerm,
          50 // Show up to 50 results
        );
        setSearchResults(response.customers || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search by 300ms
    const timeoutId = setTimeout(() => {
      searchCustomers();
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchTerm]);

  // Load appointments when editing a job
  useEffect(() => {
    if (job && isEditing && job.id) {
      loadAppointments(job.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, isEditing]);

  // Update form data when job prop changes (for editing mode)
  useEffect(() => {
    if (job && isEditing) {
      setFormData({
        description: job.description || '',
        customer_id: job.customer_id || '',
        job_category_id: job.job_category_id || '',
        status: job.status || 'active',
        price: job.price || '',
        estimated_visits: job.estimated_visits || '',
        price_type: job.price_type || 'total',
        contract_start_date: job.contract_start_date || '',
        notes: job.notes || '',
        treatment_contact_name: job.treatment_contact_name || '',
        treatment_contact_company: job.treatment_contact_company || '',
        treatment_contact_email: job.treatment_contact_email || '',
        treatment_contact_phone: job.treatment_contact_phone || '',
        treatment_address_line1: job.treatment_address_line1 || '',
        treatment_address_line2: job.treatment_address_line2 || '',
        treatment_address_city: job.treatment_address_city || '',
        treatment_address_state: job.treatment_address_state || '',
        treatment_address_postcode: job.treatment_address_postcode || '',
        treatment_address_country: job.treatment_address_country || 'United Kingdom',
        address_source: job.address_source || 'manual',
        use_customer_address: job.use_customer_address || false,
        schedule_first_appointment: false,
        appointment_date: '',
        appointment_time: '',
        appointment_duration: 60,
        appointment_notes: ''
      });

    }
  }, [job, isEditing]);

  // Auto-populate treatment contact details when customer is pre-selected
  // Note: We don't auto-enable use_customer_address - let user choose
  useEffect(() => {
    if (preSelectedCustomer && !isEditing) {
      setFormData(prev => ({
        ...prev,
        treatment_contact_name: [preSelectedCustomer.first_name, preSelectedCustomer.last_name].filter(Boolean).join(' ') || preSelectedCustomer.name || '',
        treatment_contact_company: preSelectedCustomer.company_name || '',
        treatment_contact_email: preSelectedCustomer.email || '',
        treatment_contact_phone: preSelectedCustomer.phone || ''
      }));
    }
  }, [preSelectedCustomer, isEditing]);

  // Set customer address when customer is selected and use_customer_address is enabled
  useEffect(() => {
    if (formData.use_customer_address && formData.customer_id && customers.length > 0) {
      const selectedCustomer = customers.find(c => c.id === formData.customer_id);
      if (selectedCustomer) {
        setFormData(prev => ({
          ...prev,
          treatment_contact_name: [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ') || selectedCustomer.name || '',
          treatment_contact_company: selectedCustomer.company_name || '',
          treatment_contact_email: selectedCustomer.email || '',
          treatment_contact_phone: selectedCustomer.phone || '',
          treatment_address_line1: selectedCustomer.address_line1 || '',
          treatment_address_line2: selectedCustomer.address_line2 || '',
          treatment_address_city: selectedCustomer.address_city || '',
          treatment_address_state: selectedCustomer.address_county || '',
          treatment_address_postcode: selectedCustomer.address_postcode || '',
          treatment_address_country: selectedCustomer.address_country || 'United Kingdom'
        }));
      }
    }
  }, [formData.use_customer_address, formData.customer_id, customers]);



  const loadAppointments = async (jobId) => {
    if (!user || !jobId) return;

    try {
      setLoadingAppointments(true);
      const franchiseeId = getFranchiseeIdForDataLoading(user);
      const response = await appointmentService.getAppointments(franchiseeId, {
        job_id: jobId,
        sortBy: 'scheduled_date',
        sortOrder: 'ASC'
      });
      setAppointments(response.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    const ok = await confirm({
      title: 'Delete Appointment',
      message: 'This appointment will be permanently deleted.',
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      const apt = appointments.find(a => a.id === appointmentId);
      const franchiseeId = getFranchiseeIdForCRUD(user, apt?.franchisee_id);
      await appointmentService.deleteAppointment(franchiseeId, appointmentId);
      // Reload appointments
      if (job && job.id) {
        await loadAppointments(job.id);
      }
      // Notify parent component that appointments have changed
      if (onAppointmentChange) {
        onAppointmentChange();
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment');
    }
  };

  const handleAddNewAppointment = () => {
    setShowAddAppointment(true);
    setFormData(prev => ({
      ...prev,
      schedule_first_appointment: true,
      appointment_date: '',
      appointment_time: '',
      appointment_duration: 60,
      appointment_notes: ''
    }));
  };

  const handleCancelAddAppointment = () => {
    setShowAddAppointment(false);
    setEditingAppointment(null);
    setFormData(prev => ({
      ...prev,
      schedule_first_appointment: false,
      appointment_date: '',
      appointment_time: '',
      appointment_duration: 60,
      appointment_notes: ''
    }));
  };

  const handleEditAppointment = (appointment) => {
    // Parse the scheduled_date to extract date and time
    const scheduledDate = new Date(appointment.scheduled_date);
    const dateString = scheduledDate.toISOString().split('T')[0];
    const timeString = scheduledDate.toTimeString().slice(0, 5); // HH:MM format

    setEditingAppointment(appointment);
    setShowAddAppointment(true);
    setFormData(prev => ({
      ...prev,
      schedule_first_appointment: true,
      appointment_date: dateString,
      appointment_time: timeString,
      appointment_duration: appointment.estimated_duration || 60,
      appointment_notes: appointment.notes || ''
    }));
  };

  const handleSaveAppointment = async () => {
    // Validate appointment fields
    const newErrors = {};
    if (!formData.appointment_date) {
      newErrors.appointment_date = 'Appointment date is required';
    }
    if (!formData.appointment_time) {
      newErrors.appointment_time = 'Appointment time is required';
    }
    if (formData.appointment_duration && formData.appointment_duration < 15) {
      newErrors.appointment_duration = 'Duration must be at least 15 minutes';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const franchiseeId = getFranchiseeIdForCRUD(user);

      // Combine date and time into ISO format
      const scheduledDateTime = `${formData.appointment_date}T${formData.appointment_time}:00`;

      // Calculate end date - use provided duration or default to 60 mins for "Unknown"
      let scheduledEndDate = null;
      const durationMinutes = formData.appointment_duration || 60;
      const startDate = new Date(scheduledDateTime);
      const endDate = new Date(startDate.getTime() + (durationMinutes * 60000));
      // Format as MySQL datetime (YYYY-MM-DD HH:MM:SS)
      scheduledEndDate = endDate.toISOString().slice(0, 19).replace('T', ' ');

      const appointmentData = {
        job_id: job.id,
        scheduled_date: scheduledDateTime,
        scheduled_end_date: scheduledEndDate,
        estimated_duration: formData.appointment_duration || null,
        appointment_type: 'follow_up',
        notes: formData.appointment_notes || null
      };

      if (editingAppointment) {
        // Update existing appointment
        await appointmentService.updateAppointment(franchiseeId, editingAppointment.id, appointmentData);
      } else {
        // Create new appointment
        await appointmentService.createAppointment(franchiseeId, appointmentData);
      }

      // Reload appointments
      await loadAppointments(job.id);

      // Notify parent component that appointments have changed
      if (onAppointmentChange) {
        onAppointmentChange();
      }

      // Reset form and hide
      setShowAddAppointment(false);
      setEditingAppointment(null);
      setFormData(prev => ({
        ...prev,
        schedule_first_appointment: false,
        appointment_date: '',
        appointment_time: '',
        appointment_duration: 60,
        appointment_notes: ''
      }));
      setErrors({});
    } catch (error) {
      console.error(`Error ${editingAppointment ? 'updating' : 'creating'} appointment:`, error);
      alert(`Failed to ${editingAppointment ? 'update' : 'create'} appointment`);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // If unchecking "use_customer_address", clear the address fields
    if (name === 'use_customer_address' && !checked) {
      setFormData(prev => ({
        ...prev,
        [name]: false,
        treatment_address_line1: '',
        treatment_address_line2: '',
        treatment_address_city: '',
        treatment_address_state: '',
        treatment_address_postcode: '',
        treatment_address_country: 'United Kingdom'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle time picker changes (separate hour and minute)
  const handleTimeChange = (type, value) => {
    const [currentHour, currentMinute] = formData.appointment_time ? formData.appointment_time.split(':') : ['', ''];

    let newHour = type === 'hour' ? value : currentHour;
    let newMinute = type === 'minute' ? value : currentMinute;

    // If no time is set yet and user clicks, default to current time
    if (!formData.appointment_time) {
      const defaultTime = getCurrentTimeRounded();
      newHour = type === 'hour' ? value : defaultTime.hour;
      newMinute = type === 'minute' ? value : defaultTime.minute;
    }

    const newTime = `${newHour}:${newMinute}`;
    setFormData(prev => ({
      ...prev,
      appointment_time: newTime
    }));

    if (errors.appointment_time) {
      setErrors(prev => ({
        ...prev,
        appointment_time: ''
      }));
    }
  };

  const handleCategorySelect = (categoryId, categoryPath) => {
    setFormData(prev => ({
      ...prev,
      job_category_id: categoryId
    }));
    setSelectedCategoryPath(categoryPath);
    
    // Clear category error if exists
    if (errors.job_category_id) {
      setErrors(prev => ({
        ...prev,
        job_category_id: ''
      }));
    }
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      treatment_address_line1: addressData.address_line1 || '',
      treatment_address_line2: addressData.address_line2 || '',
      treatment_address_city: addressData.address_city || '',
      treatment_address_state: addressData.address_county || '',
      treatment_address_postcode: addressData.address_postcode || '',
      treatment_address_country: addressData.address_country || 'United Kingdom',
      address_source: 'custom_address'  // Fixed: must be 'custom_address' or 'customer_address'
    }));

    // Clear any address-related errors
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.treatment_address;
      return newErrors;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = 'Customer selection is required';
    }

    if (!formData.job_category_id) {
      newErrors.job_category_id = 'Job type is required';
    }
    
    // Validate price if provided
    if (formData.price && (isNaN(formData.price) || parseFloat(formData.price) < 0)) {
      newErrors.price = 'Price must be a valid positive number';
    }
    
    // Validate email format if provided
    if (formData.treatment_contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.treatment_contact_email)) {
      newErrors.treatment_contact_email = 'Invalid email format';
    }
    
    // Validate appointment fields if scheduling first appointment
    if (formData.schedule_first_appointment) {
      if (!formData.appointment_date) {
        newErrors.appointment_date = 'Appointment date is required';
      }
      if (!formData.appointment_time) {
        newErrors.appointment_time = 'Appointment time is required';
      }
      // Only validate duration if one is provided (allow "Unknown"/empty)
      if (formData.appointment_duration && formData.appointment_duration < 15) {
        newErrors.appointment_duration = 'Duration must be at least 15 minutes';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        estimated_visits: formData.estimated_visits ? parseInt(formData.estimated_visits) : null,
        price_type: formData.price_type || 'total',
        contract_start_date: formData.contract_start_date || null,
        is_contract: isContract,
        appointment_duration: formData.schedule_first_appointment ? parseInt(formData.appointment_duration) : null
      };

      try {
        await onSubmit(submitData);

        // Reset form only on success and if not editing
        if (!isEditing) {
          setFormData({
            description: '',
            customer_id: preSelectedCustomer?.id || '',
            job_category_id: '',
            status: 'active',
            price: '',
            estimated_visits: '',
            price_type: 'total',
            contract_start_date: '',
            notes: '',
            treatment_contact_name: '',
            treatment_contact_company: '',
            treatment_contact_email: '',
            treatment_contact_phone: '',
            treatment_address_line1: '',
            treatment_address_line2: '',
            treatment_address_city: '',
            treatment_address_state: '',
            treatment_address_postcode: '',
            treatment_address_country: 'United Kingdom',
            address_source: 'manual',
            use_customer_address: false,
            technician_id: '',
            schedule_first_appointment: false,
            appointment_date: '',
            appointment_time: '',
            appointment_duration: 60,
            appointment_notes: ''
          });
          setSelectedCategoryPath('');
        }
      } catch (error) {
        // Error toast handled by caller — keep modal open for retry
      }
    }
  };

  const selectedCustomer = customers.find(c => c.id === formData.customer_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">
              {isEditing ? `Edit Job - ${job?.category_name || 'Job'}` : isContract ? 'Add New Contract' : 'Add New Job'}
            </h2>
            {selectedCustomer && isEditing && (
              <p className="text-base text-white mt-0.5">
                <span className="font-semibold">{selectedCustomer.name}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-4 md:p-8">
            {/* Job / Contract Toggle - only when adding */}
            {!isEditing && (
              <div className="flex items-center justify-center mb-6">
                <div className="inline-flex bg-gray-100 rounded-xl p-1 shadow-inner">
                  <button
                    type="button"
                    onClick={() => {
                      if (isContract) {
                        setIsContract(false);
                        setFormData(prev => ({ ...prev, job_category_id: '', price_type: 'total', estimated_visits: '', contract_start_date: '' }));
                        setSelectedCategoryPath('');
                      }
                    }}
                    className={`flex items-center gap-2 px-8 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                      !isContract
                        ? 'bg-white shadow-md text-pest-blue ring-1 ring-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Briefcase size={16} />
                    Standard Job
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isContract) {
                        setIsContract(true);
                        setFormData(prev => ({
                          ...prev,
                          job_category_id: CONTRACT_SERVICES_PARENT_ID,
                          price_type: 'per_year',
                          estimated_visits: prev.estimated_visits || 4,
                          contract_start_date: prev.contract_start_date || new Date().toISOString().split('T')[0]
                        }));
                        setSelectedCategoryPath('Contract Services');
                      }
                    }}
                    className={`flex items-center gap-2 px-8 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                      isContract
                        ? 'bg-white shadow-md text-pest-blue ring-1 ring-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <FileText size={16} />
                    Contract
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
              {/* Left Column - Job Details & Customer */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-pest-blue">Job Details</h3>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    {/* Job Category Picker - always use the popup picker */}
                    <JobCategoryPicker
                      selectedCategoryId={formData.job_category_id}
                      onCategorySelect={handleCategorySelect}
                      error={errors.job_category_id}
                      disabled={false}
                    />

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors resize-none"
                        placeholder="Enter job description..."
                      />
                    </div>

                    <div className={`grid grid-cols-1 ${isAdmin ? 'sm:grid-cols-2' : ''} gap-4`}>
                      <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          id="status"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                        >
                          {statusOptions.map(status => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {isAdmin && (
                        <div>
                          <label htmlFor="technician_id" className="block text-sm font-medium text-gray-700 mb-2">
                            Assign Technician
                          </label>
                          <select
                            id="technician_id"
                            name="technician_id"
                            value={formData.technician_id}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                          >
                            <option value="">Select technician...</option>
                            {technicians.map(tech => (
                              <option key={tech.id} value={tech.id}>
                                {tech.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                        {formData.price_type === 'per_visit' ? 'Price per Visit (£)' : formData.price_type === 'per_year' ? 'Price per Year (£)' : 'Total Price (£)'}
                      </label>
                      <input
                        type="number"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                          errors.price ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                        }`}
                        placeholder="0.00"
                      />
                      {errors.price && <p className="text-red-500 text-sm mt-2">{errors.price}</p>}
                    </div>

                    {/* Price Type Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price Type
                      </label>
                      <div className="flex rounded-lg border-2 border-gray-300 overflow-hidden">
                        {isContract ? (
                          <>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, price_type: 'per_visit' }))}
                              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                                formData.price_type === 'per_visit'
                                  ? 'bg-pest-blue text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Per Visit
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, price_type: 'per_year' }))}
                              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                                formData.price_type === 'per_year'
                                  ? 'bg-pest-blue text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Per Year
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, price_type: 'total' }))}
                              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                                formData.price_type === 'total'
                                  ? 'bg-pest-blue text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Total Price
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, price_type: 'per_visit' }))}
                              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                                formData.price_type === 'per_visit'
                                  ? 'bg-pest-blue text-white'
                                  : 'bg-white text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Per Visit
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Estimated Visits */}
                    <div>
                      <label htmlFor="estimated_visits" className="block text-sm font-medium text-gray-700 mb-2">
                        Estimated Visits
                      </label>
                      <select
                        id="estimated_visits"
                        name="estimated_visits"
                        value={formData.estimated_visits}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                      >
                        <option value="">Not set</option>
                        {Array.from({ length: 48 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                    </div>

                    {/* Contract Start Date - only in contract mode */}
                    {isContract && (
                      <div>
                        <label htmlFor="contract_start_date" className="block text-sm font-medium text-gray-700 mb-2">
                          Contract Start Date
                        </label>
                        <DateTimePicker
                          value={formData.contract_start_date}
                          onChange={(val) => handleChange({ target: { name: 'contract_start_date', value: val } })}
                          dateOnly
                          className="w-full"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {!isEditing && !selectedCustomer && (
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-pest-blue">Customer</h3>
                  </div>

                  <div className="p-6 space-y-4">
                    {!selectedCustomer && !showCustomerSelector && (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setShowCustomerSelector(true)}
                          className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-pest-blue hover:bg-blue-50 transition-all text-gray-600 hover:text-pest-blue font-medium"
                        >
                          <Search className="inline w-5 h-5 mr-2" />
                          Select Customer
                        </button>
                        {errors.customer_id && <p className="text-red-500 text-sm mt-2">{errors.customer_id}</p>}
                      </div>
                    )}

                    {!selectedCustomer && showCustomerSelector && (
                      <div className="space-y-3">
                        <div>
                          <label htmlFor="customer_search" className="block text-sm font-medium text-gray-700 mb-2">
                            Search Customer *
                          </label>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="text"
                              id="customer_search"
                              value={customerSearchTerm}
                              onChange={(e) => setCustomerSearchTerm(e.target.value)}
                              placeholder="Type at least 2 characters to search..."
                              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                              autoFocus
                            />
                          </div>
                          {customerSearchTerm.length > 0 && customerSearchTerm.length < 2 && (
                            <p className="text-sm text-gray-500 mt-1">Type at least 2 characters to search</p>
                          )}
                        </div>

                        {isSearching && (
                          <div className="text-center py-4">
                            <div className="inline-flex items-center">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pest-blue mr-2"></div>
                              <span className="text-gray-600">Searching...</span>
                            </div>
                          </div>
                        )}

                        {!isSearching && customerSearchTerm.length >= 2 && (
                          <div className="max-h-60 overflow-y-auto border-2 border-gray-200 rounded-lg">
                            {searchResults.length === 0 ? (
                              <div className="px-4 py-6 text-center text-gray-500">
                                No customers found matching "{customerSearchTerm}"
                              </div>
                            ) : (
                              searchResults.map(customer => (
                                <button
                                  key={customer.id}
                                  type="button"
                                  onClick={async () => {
                                    // Add the selected customer to the customers array
                                    setCustomers(prev => {
                                      const exists = prev.find(c => c.id === customer.id);
                                      return exists ? prev : [...prev, customer];
                                    });
                                    setFormData(prev => ({ ...prev, customer_id: customer.id }));
                                    setShowCustomerSelector(false);
                                    setCustomerSearchTerm('');
                                    setSearchResults([]);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                >
                                  <div className="font-medium text-gray-900">{customer.name}</div>
                                  {customer.company_name && (
                                    <div className="text-sm text-gray-600">{customer.company_name}</div>
                                  )}
                                  {customer.email && (
                                    <div className="text-sm text-gray-500">{customer.email}</div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomerSelector(false);
                            setCustomerSearchTerm('');
                            setSearchResults([]);
                          }}
                          className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                        >
                          Cancel
                        </button>
                        {errors.customer_id && <p className="text-red-500 text-sm mt-2">{errors.customer_id}</p>}
                      </div>
                    )}

                    {selectedCustomer && (
                      <div>
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center">
                              <User className="w-5 h-5 text-blue-600 mr-2" />
                              <h4 className="text-sm font-semibold text-blue-900">Selected Customer</h4>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, customer_id: '' }));
                                setShowCustomerSelector(true);
                                setCustomerSearchTerm('');
                                setSearchResults([]);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                            >
                              Change
                            </button>
                          </div>
                          <div className="text-sm text-blue-900 space-y-1 ml-7">
                            <p className="font-semibold">{selectedCustomer.name}</p>
                            {selectedCustomer.company_name && (
                              <p className="text-blue-700">{selectedCustomer.company_name}</p>
                            )}
                            {selectedCustomer.email && (
                              <p className="text-blue-700">{selectedCustomer.email}</p>
                            )}
                            {selectedCustomer.phone && (
                              <p className="text-blue-700">{selectedCustomer.phone}</p>
                            )}
                            {selectedCustomer.address_line1 && (
                              <p className="text-blue-700">{formatCustomerAddress(selectedCustomer)}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>

              {/* Right Column - Treatment Contact & Address */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-pest-blue">Treatment Contact</h3>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    {/* Row 1: Contact Name + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="treatment_contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                          Contact Name
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="treatment_contact_name"
                            name="treatment_contact_name"
                            value={formData.treatment_contact_name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                            placeholder="Contact name"
                          />
                          {formData.treatment_contact_name && (
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, treatment_contact_name: '' }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
                          )}
                        </div>
                      </div>
                      <div>
                        <label htmlFor="treatment_contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone
                        </label>
                        <div className="relative">
                          <input
                            type="text" inputMode="tel"
                            id="treatment_contact_phone"
                            name="treatment_contact_phone"
                            value={formData.treatment_contact_phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                            placeholder="Contact phone"
                          />
                          {formData.treatment_contact_phone && (
                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, treatment_contact_phone: '' }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Company (full width) */}
                    <div>
                      <label htmlFor="treatment_contact_company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          id="treatment_contact_company"
                          name="treatment_contact_company"
                          value={formData.treatment_contact_company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 pr-9 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors"
                          placeholder="Company name"
                        />
                        {formData.treatment_contact_company && (
                          <button type="button" onClick={() => setFormData(prev => ({ ...prev, treatment_contact_company: '' }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
                        )}
                      </div>
                    </div>

                    {/* Row 3: Email (full width) */}
                    <div>
                      <label htmlFor="treatment_contact_email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="treatment_contact_email"
                          name="treatment_contact_email"
                          value={formData.treatment_contact_email}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 pr-9 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                            errors.treatment_contact_email ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Contact email"
                        />
                        {formData.treatment_contact_email && (
                          <button type="button" onClick={() => setFormData(prev => ({ ...prev, treatment_contact_email: '' }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"><X size={14} /></button>
                        )}
                      </div>
                      {errors.treatment_contact_email && <p className="text-red-500 text-sm mt-2">{errors.treatment_contact_email}</p>}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-pest-blue">Treatment Address</h3>
                  </div>

                  <div className="p-6">
                    <div className="space-y-4">
                      {/* Checkbox at the top - only show if customer has address */}
                      {selectedCustomer && selectedCustomer.address_line1 && (
                        <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition">
                          <input
                            type="checkbox"
                            name="use_customer_address"
                            checked={formData.use_customer_address}
                            onChange={handleChange}
                            className="w-4 h-4 text-pest-blue border-gray-300 rounded focus:ring-2 focus:ring-pest-blue"
                          />
                          <span className="ml-3 text-sm">
                            <span className="font-medium text-gray-700">Use customer address</span>
                            <span className="ml-2 text-gray-500 text-xs">
                              ({selectedCustomer.address_line1}, {selectedCustomer.address_postcode})
                            </span>
                          </span>
                        </label>
                      )}

                      {/* Show green box when checkbox is checked, otherwise show AddressFinder */}
                      {formData.use_customer_address && selectedCustomer ? (
                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <svg className="w-5 h-5 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                            </div>
                            <div className="ml-3 flex-1">
                              <p className="text-sm font-semibold text-green-900 mb-1">Using Customer Address</p>
                              <div className="text-sm text-green-800 space-y-0.5">
                                {selectedCustomer.address_line1 && <div>{selectedCustomer.address_line1}</div>}
                                {selectedCustomer.address_line2 && <div>{selectedCustomer.address_line2}</div>}
                                {selectedCustomer.address_city && <div>{selectedCustomer.address_city}</div>}
                                {selectedCustomer.address_county && <div>{selectedCustomer.address_county}</div>}
                                {selectedCustomer.address_postcode && <div>{selectedCustomer.address_postcode}</div>}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <AddressFinder
                          initialAddress={{
                            address_line1: formData.treatment_address_line1,
                            address_line2: formData.treatment_address_line2,
                            address_city: formData.treatment_address_city,
                            address_county: formData.treatment_address_state,
                            address_postcode: formData.treatment_address_postcode,
                            address_country: formData.treatment_address_country
                          }}
                          onAddressSelect={handleAddressSelect}
                          onError={(error) => {
                            setErrors(prev => ({
                              ...prev,
                              treatment_address: error
                            }));
                          }}
                          showManualEntry={true}
                          autoSelectSingle={true}

                        />
                      )}
                    </div>

                    {errors.treatment_address && <p className="text-red-500 text-sm mt-2">{errors.treatment_address}</p>}
                  </div>
                </div>

                {/* Appointments Section - only shown when adding, not editing */}
                {!isEditing && (
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-pest-blue">Appointments</h3>
                  </div>

                  <div className="p-6">
                    {/* Show loading state */}
                    {loadingAppointments && (
                      <div className="text-center py-4 text-gray-600">
                        Loading appointments...
                      </div>
                    )}

                    {/* Show existing appointments list when editing */}
                    {isEditing && !loadingAppointments && appointments.length > 0 && !showAddAppointment && (
                      <div className="space-y-3">
                        {appointments.map((appointment) => (
                          <div key={appointment.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-pest-blue hover:bg-blue-50 transition-colors cursor-pointer group">
                            <div
                              className="flex-1"
                              onClick={() => handleEditAppointment(appointment)}
                            >
                              <div className="flex items-center gap-2 flex-wrap text-sm font-medium text-gray-900">
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} className="text-pest-blue" />
                                  {new Date(appointment.scheduled_date).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock size={14} className="text-pest-blue" />
                                  {new Date(appointment.scheduled_date).toLocaleTimeString('en-GB', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                {appointment.estimated_duration && (
                                  <span className="text-xs text-gray-600">
                                    • Estimated duration: {formatDuration(appointment.estimated_duration)}
                                  </span>
                                )}
                              </div>
                              {appointment.notes && (
                                <div className="text-xs text-gray-600 mt-1 italic">
                                  {appointment.notes}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAppointment(appointment.id);
                              }}
                              className="text-red-500 hover:text-red-700 transition-colors p-1 opacity-0 group-hover:opacity-100"
                              title="Delete appointment"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}

                        {/* Add Appointment button after existing appointments */}
                        <button
                          type="button"
                          onClick={handleAddNewAppointment}
                          className="w-full px-4 py-3 border-2 border-dashed border-pest-blue rounded-lg hover:bg-blue-50 transition-all text-pest-blue font-medium"
                        >
                          <Plus className="inline w-5 h-5 mr-2" />
                          Add Appointment
                        </button>
                      </div>
                    )}

                    {/* Show "Add Appointment" button for editing with no appointments */}
                    {isEditing && !loadingAppointments && appointments.length === 0 && !showAddAppointment && (
                      <button
                        type="button"
                        onClick={handleAddNewAppointment}
                        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-pest-blue hover:bg-blue-50 transition-all text-gray-600 hover:text-pest-blue font-medium"
                      >
                        <Plus className="inline w-5 h-5 mr-2" />
                        Add First Appointment
                      </button>
                    )}

                    {/* Show checkbox for new jobs or when adding appointment to existing job */}
                    {(!isEditing || showAddAppointment) && (
                      <>
                        {!isEditing && (
                          <div className="flex items-center mb-4">
                            <input
                              type="checkbox"
                              id="schedule_first_appointment"
                              name="schedule_first_appointment"
                              checked={formData.schedule_first_appointment}
                              onChange={handleChange}
                              className="mr-3 w-4 h-4 text-pest-blue border-gray-300 rounded focus:ring-2 focus:ring-pest-blue"
                            />
                            <label htmlFor="schedule_first_appointment" className="text-sm font-medium text-gray-700 cursor-pointer">
                              Schedule First Appointment (Optional)
                            </label>
                          </div>
                        )}
                        {showAddAppointment && (
                          <div className="mb-4 text-sm font-medium text-gray-700">
                            {editingAppointment ? 'Edit Appointment' : 'Add New Appointment'}
                          </div>
                        )}
                      </>
                    )}

                    {/* Appointment form fields */}
                    {(formData.schedule_first_appointment || showAddAppointment) && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="inline w-4 h-4 mr-2" />
                            Date & Time *
                          </label>
                          <DateTimePicker
                            value={formData.appointment_date && formData.appointment_time
                              ? `${formData.appointment_date}T${formData.appointment_time}`
                              : formData.appointment_date || ''}
                            onChange={(val) => {
                              if (!val) {
                                handleChange({ target: { name: 'appointment_date', value: '' } });
                                handleChange({ target: { name: 'appointment_time', value: '' } });
                              } else {
                                const [date, time] = val.split('T');
                                handleChange({ target: { name: 'appointment_date', value: date || '' } });
                                handleChange({ target: { name: 'appointment_time', value: time || '' } });
                              }
                            }}
                            className="w-full"
                          />
                          {(errors.appointment_date || errors.appointment_time) && (
                            <p className="text-red-500 text-sm mt-2">{errors.appointment_date || errors.appointment_time}</p>
                          )}
                        </div>

                      <div>
                        <label htmlFor="appointment_duration" className="block text-sm font-medium text-gray-700 mb-2">
                          Estimated duration
                        </label>
                        <select
                          id="appointment_duration"
                          name="appointment_duration"
                          value={formData.appointment_duration}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors cursor-pointer ${
                            errors.appointment_duration ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <option value="">Unknown</option>
                          <option value="15">15 Mins</option>
                          <option value="20">20 Mins</option>
                          <option value="25">25 Mins</option>
                          <option value="30">30 Mins</option>
                          <option value="35">35 Mins</option>
                          <option value="40">40 Mins</option>
                          <option value="45">45 Mins</option>
                          <option value="50">50 Mins</option>
                          <option value="55">55 Mins</option>
                          <option value="60">1 Hour</option>
                          <option value="75">1 Hour 15 Mins</option>
                          <option value="90">1 Hour 30 Mins</option>
                          <option value="105">1 Hour 45 Mins</option>
                          <option value="120">2 Hours</option>
                          <option value="150">2 Hours 30 Mins</option>
                          <option value="180">3 Hours</option>
                          <option value="210">3 Hours 30 Mins</option>
                          <option value="240">4 Hours</option>
                          <option value="300">5 Hours</option>
                          <option value="360">6 Hours</option>
                          <option value="420">7 Hours</option>
                          <option value="480">8 Hours</option>
                        </select>
                        {errors.appointment_duration && <p className="text-red-500 text-sm mt-2">{errors.appointment_duration}</p>}
                      </div>

                        <div>
                          <label htmlFor="appointment_notes" className="block text-sm font-medium text-gray-700 mb-2">
                            Appointment Notes
                          </label>
                          <textarea
                            id="appointment_notes"
                            name="appointment_notes"
                            value={formData.appointment_notes}
                            onChange={handleChange}
                            rows={3}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors resize-none"
                            placeholder="Additional notes for this appointment..."
                          />
                        </div>

                        {/* Action buttons when adding appointment in edit mode */}
                        {showAddAppointment && (
                          <div className="flex gap-3">
                            <button
                              type="button"
                              onClick={handleCancelAddAppointment}
                              className="flex-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveAppointment}
                              className="flex-1 px-4 py-2 text-sm text-white font-semibold bg-gradient-to-r from-pest-green to-green-600 rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg"
                            >
                              {editingAppointment ? 'Update Appointment' : 'Save Appointment'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                )}

              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-4 space-y-3 sm:space-y-0">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-lg btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-lg btn-primary"
                >
                  {isEditing ? 'Update Job' : isContract ? 'Create Contract' : 'Create Job'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default AddJobModal;